import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { openUtilityApp, retryUtilityApp, reloadUtilityApp } from '../utils/utilityAppStorage';
import { rehydrateExercises } from './fitness/store/exerciseStore';
import { rehydrateRoutines } from './fitness/store/routineStore';
import { rehydrateNutrition } from './fitness/store/nutritionStore';
import { rehydrateLoadPlans } from './fitness/store/loadPlanStore';
import { useWorkoutStore } from './fitness/store/workoutStore';
import { useProgressStore } from './fitness/store/progressStore';

const routes = {
  '/apps/growth-planner': 'growth_planner', '/apps/fitness': 'fitness',
  '/apps/compras': 'libro_compras', '/apps/ventas': 'libro_ventas',
  '/apps/gastos': 'libro_gastos', '/apps/fastfood': 'fastfood_pos_v1',
  '/apps/tools-dashboard': 'business_books',
};
const appsFor = app => app === 'business_books' ? ['libro_compras', 'libro_ventas', 'libro_gastos'] : [app];
export default function TransactionalAppBoundary({ children }) {
  const { pathname } = useLocation();
  const account = useAuthStore(state => state.user);
  const user = account?._id;
  const fastFoodAccess = account?.apps?.fastfood_pos_v1 || account?.appsAccess?.fastfood_pos_v1;
  const requestedApp = routes[pathname];
  const app = requestedApp === 'fastfood_pos_v1' && (!fastFoodAccess?.enabled || !(new Date(fastFoodAccess.expiresAt) > new Date())) ? null : requestedApp;
  const identity = `${user || 'guest'}:${app}`;
  const [ready, setReady] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError(null);
    if (!app || !user) { setReady(identity); return; }
    Promise.all(appsFor(app).map(openUtilityApp)).then(() => {
      if (!active) return;
      if (app === 'fitness') {
        const data = JSON.parse(localStorage.getItem('fitnessData') || '{}');
        rehydrateExercises();
        rehydrateRoutines();
        rehydrateNutrition();
        rehydrateLoadPlans();
        useWorkoutStore.setState({ activeWorkout: data.activeWorkout || null, history: data.history || [] });
        useProgressStore.setState({ measurements: data.progress?.measurements || [], workoutStreak: data.progress?.workoutStreak || 0, lastWorkoutDate: data.progress?.lastWorkoutDate || null });
      }
      setReady(identity);
    }).catch(err => { if (active) setError(err.response?.data?.message || err.message); });
    return () => { active = false; };
  }, [app, user, identity, attempt]);
  useEffect(() => {
    const onStatus = event => { if (appsFor(app).includes(event.detail.app)) setError(event.detail.error); };
    window.addEventListener('app-transaction-status', onStatus);
    return () => window.removeEventListener('app-transaction-status', onStatus);
  }, [app]);
  if (!app) return children;
  const retry = async () => {
    try {
      if (ready === identity) await Promise.all(appsFor(app).map(retryUtilityApp));
      else setAttempt(value => value + 1);
    } catch (err) { setError(err.response?.data?.message || err.message); }
  };
  const reload = async () => {
    setReady(null);
    try {
      await Promise.all(appsFor(app).map(reloadUtilityApp));
      setAttempt(value => value + 1);
    } catch (err) { setError(err.response?.data?.message || err.message); }
  };
  return <Box>
    {error && <Alert severity="error" action={<Box><Button color="inherit" onClick={retry}>Reintentar</Button><Button color="inherit" onClick={reload}>Cargar nube y respaldar cambios locales</Button></Box>}>{error} Tus cambios locales se conservan.</Alert>}
    {ready === identity ? <Box key={`${identity}:${attempt}`}>{children}</Box> : !error && <CircularProgress aria-label="Cargando datos" />}
  </Box>;
}
